import { useState, useCallback, useMemo, ReactNode } from "react";
import { AlertModal } from "../components/ui/AlertModal";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import { Toast } from "../components/ui/Toast";
import {
  useUIState,
  useUIDispatch,
  UIStateContext,
  UIDispatchContext,
  AlertState,
  ConfirmState,
  ToastState,
} from "../hooks/useUI";

// --- Initial States ---

const initialAlert: AlertState = {
  isOpen: false,
  title: "",
  message: "",
  type: "info",
};

const initialConfirm: ConfirmState = {
  isOpen: false,
  title: "",
  message: "",
  type: "danger",
  onConfirm: () => {},
};

const initialToast: ToastState = {
  isVisible: false,
  title: "",
  message: "",
  type: "info",
};

// --- UI Singletons Renderer ---

function UISingletons() {
  const { alert, confirm, toast } = useUIState();
  const { hideAlert, hideConfirm, hideToast } = useUIDispatch();

  return (
    <>
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => {
          if (alert.onClose) alert.onClose();
          hideAlert();
        }}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={confirm.isOpen}
        onClose={() => {
          if (confirm.onClose) confirm.onClose();
          hideConfirm();
        }}
        onConfirm={confirm.onConfirm}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        confirmText={confirm.confirmText || "Confirm"}
        cancelText={confirm.cancelText || "Cancel"}
      />
      {toast.isVisible && (
        <Toast
          title={toast.title}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={toast.duration}
        />
      )}
    </>
  );
}

// --- Provider ---

export function UIProvider({ children }: { children: ReactNode }) {
  const [alert, setAlert] = useState<AlertState>(initialAlert);
  const [confirm, setConfirm] = useState<ConfirmState>(initialConfirm);
  const [toast, setToast] = useState<ToastState>(initialToast);

  const showAlert = useCallback((config: Omit<AlertState, "isOpen">) => {
    setAlert({ ...config, isOpen: true } as AlertState);
  }, []);

  const hideAlert = useCallback(() => {
    setAlert((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const showConfirm = useCallback((config: Omit<ConfirmState, "isOpen">) => {
    setConfirm({ ...config, isOpen: true } as ConfirmState);
  }, []);

  const hideConfirm = useCallback(() => {
    setConfirm((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const showToast = useCallback((config: Omit<ToastState, "isVisible">) => {
    setToast({ ...config, isVisible: true } as ToastState);
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  }, []);

  const dispatch = useMemo(
    () => ({
      showAlert,
      hideAlert,
      showConfirm,
      hideConfirm,
      showToast,
      hideToast,
    }),
    [showAlert, hideAlert, showConfirm, hideConfirm, showToast, hideToast],
  );

  const state = useMemo(
    () => ({ alert, confirm, toast }),
    [alert, confirm, toast],
  );

  return (
    <UIDispatchContext.Provider value={dispatch}>
      <UIStateContext.Provider value={state}>
        {children}
        <UISingletons />
      </UIStateContext.Provider>
    </UIDispatchContext.Provider>
  );
}
